import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layers,
  Shield,
  HardDrive,
  Camera,
  KeyRound,
  BookOpen,
  PenLine,
  DollarSign,
  User,
  LogIn,
  UserPlus,
  ArrowLeft,
  Book,
  LayoutDashboard,
  Newspaper,

} from 'lucide-react';

const Sidebar = ({ activePage, isMobile, isTablet, isSidebarOpen, toggleSidebar, authenticated, onAuthAction }) => {
  
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile || isTablet) {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target) && 
            !event.target.closest('.hamburger-button')) {
          if (isSidebarOpen) {
            toggleSidebar();
          }
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isTablet, isSidebarOpen, toggleSidebar]);

  const getMenuSections = () => {
    if (authenticated) {
      if (isMobile || isTablet) {
        return [
          {
            title: "Resources",
            items: [
{ id: 'docs', label: 'Documentation', icon: <Book size={18} strokeWidth={1.5} />, path: 'https://docs.barrack.ai' },
{ id: 'blog', label: 'Blog', icon: <Newspaper size={18} strokeWidth={1.5} />, path: 'https://blog.barrack.ai' },
              { id: 'pricing', label: 'Pricing', icon: <DollarSign size={18} strokeWidth={1.5} />, path: '/pricing' }
            ]
          },
          {
            title: "Overview", 
            items: [
              { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} strokeWidth={1.5} />, path: '/dashboard' }
            ]
          },
          {
            title: "VM Management",
            items: [
              { id: 'images', label: 'Images', icon: <Layers size={18} strokeWidth={1.5} />, path: '/images' },
              { id: 'firewall', label: 'Firewall', icon: <Shield size={18} strokeWidth={1.5} />, path: '/firewall' },
              { id: 'volumes', label: 'Volumes', icon: <HardDrive size={18} strokeWidth={1.5} />, path: '/volumes' },
              { id: 'snapshots', label: 'Snapshots', icon: <Camera size={18} strokeWidth={1.5} />, path: '/snapshots' },
              { id: 'ssh-keys', label: 'SSH Keys', icon: <KeyRound size={18} strokeWidth={1.5} />, path: '/ssh-keys' },
              { id: 'account', label: 'My Account', icon: <User size={18} strokeWidth={1.5} />, path: '/account' }
            ]
          }
        ];
      } else {
        return [
          {
            title: "VM Management",
            items: [
              { id: 'images', label: 'Images', icon: <Layers size={18} strokeWidth={1.5} />, path: '/images' },
              { id: 'firewall', label: 'Firewall', icon: <Shield size={18} strokeWidth={1.5} />, path: '/firewall' },
              { id: 'volumes', label: 'Volumes', icon: <HardDrive size={18} strokeWidth={1.5} />, path: '/volumes' },
              { id: 'snapshots', label: 'Snapshots', icon: <Camera size={18} strokeWidth={1.5} />, path: '/snapshots' },
              { id: 'ssh-keys', label: 'SSH Keys', icon: <KeyRound size={18} strokeWidth={1.5} />, path: '/ssh-keys' },
              { id: 'account', label: 'My Account', icon: <User size={18} strokeWidth={1.5} />, path: '/account' }
            ]
          }
        ];
      }
    } else {
      return [
        {
          title: "Resources",
          items: [
{ id: 'docs', label: 'Documentation', icon: <Book size={18} strokeWidth={1.5} />, path: 'https://docs.barrack.ai' },
{ id: 'blog', label: 'Blog', icon: <Newspaper size={18} strokeWidth={1.5} />, path: 'https://blog.barrack.ai' },
            { id: 'pricing', label: 'Pricing', icon: <DollarSign size={18} strokeWidth={1.5} />, path: '/pricing' }
          ]
        },
        {
          title: "Get Started",
          items: [
            { id: 'login', label: 'Login', icon: <LogIn size={18} strokeWidth={1.5} />, path: '/login' },
            { id: 'signup', label: 'Sign Up', icon: <UserPlus size={18} strokeWidth={1.5} />, path: '/signup' }
          ]
        }
      ];
    }
  };
  
  const menuSections = getMenuSections();
  
  const handleItemClick = (item) => {
    if (item.id === 'login' || item.id === 'signup') {
      if (onAuthAction) {
        onAuthAction(item.id);
      }
    }
    
    if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
      window.open(item.path, '_blank');
    } else {
      navigate(item.path);
    }
    
    if ((isMobile || isTablet) && isSidebarOpen) {
      toggleSidebar();
    }
  };
  
  const closeSidebar = () => {
    if (isSidebarOpen) {
      toggleSidebar();
    }
  };
  
  const renderSidebarContent = () => (
    <>
      {(isMobile || isTablet) && (
        <div className="flex items-center justify-between p-4 border-b border-[#222222] mb-2">
          <span className="text-white font-medium">Menu</span>
          <button 
            onClick={closeSidebar}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} strokeWidth={1.5} />
          </button>
        </div>
      )}
      
      <div className="py-2">
        {menuSections.map((section, index) => (
          <div key={section.title} className="mb-6">
            {section.items.map((item) => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`
                  flex items-center py-3.5 px-5 my-1.5 mx-2
                  rounded-md transition-all duration-200
                  hover:bg-[#151515] cursor-pointer
                  ${activePage === item.id
                   ? 'bg-[#151515] border-l-4 border-[#e0eacc] text-white shadow-sm'
                   : 'text-gray-400 hover:text-gray-200 border-l-4 border-transparent'
                  }
                `}
              >
                <span className={`mr-3 ${activePage === item.id ? 'text-[#e0eacc]' : 'text-gray-500'}`}>
                  {item.icon}
                </span>
                <span className={`font-medium ${activePage === item.id ? 'text-white' : ''}`}>
                  {item.label}
                </span>
              </div>
            ))}
            
            {index < menuSections.length - 1 && (
              <div className="mx-5 my-3 border-t border-[#222222]"></div>
            )}
          </div>
        ))}
      </div>
    </>
  );
  
  if (!isMobile && !isTablet) {
    return (
      <div className="bg-[#090909] h-screen w-[13.5%] min-w-[110px] flex flex-col py-4 border-r border-[#1a1a1a] shadow-lg mt-8 sticky top-8">
        {renderSidebarContent()}
      </div>
    );
  }
  
  if (!isSidebarOpen) return null;
  
  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={closeSidebar}
      />
      
      <div
        ref={sidebarRef}
        className="fixed inset-y-0 left-0 z-50 w-64 bg-[#0f0f0f] border-r border-[#222222] transform transition-transform duration-300 ease-in-out shadow-xl translate-x-0"
      >
        <div className="h-full flex flex-col">
          {renderSidebarContent()}
        </div>
      </div>
    </>
  );
};

export default Sidebar;